import type { ChatSseEvent } from './types';
import type { ChatTurnDebugTrace } from './turn-debug';

function parseEventBlock(block: string): ChatSseEvent | null {
  let eventName = 'message';
  let data = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      data += line.slice(5).trim();
    }
  }

  if (!data) {
    return null;
  }

  try {
    const payload = JSON.parse(data) as Record<string, unknown>;
    switch (eventName) {
      case 'token':
        return { type: 'token', delta: String(payload.delta ?? '') };
      case 'tool_call': {
        const args = (payload.args as Record<string, unknown>) ?? {};
        if (typeof payload.targetTitle === 'string' && payload.targetTitle.trim()) {
          args.targetTitle = payload.targetTitle.trim();
        }
        return {
          type: 'tool_call',
          name: String(payload.name ?? ''),
          args,
        };
      }
      case 'tool_result':
        return {
          type: 'tool_result',
          name: String(payload.name ?? ''),
          ok: Boolean(payload.ok),
          summary: String(payload.summary ?? ''),
        };
      case 'message':
        return { type: 'message', id: String(payload.id ?? '') };
      case 'error':
        return {
          type: 'error',
          code: String(payload.code ?? 'ERROR'),
          message: String(payload.message ?? 'Unknown error'),
        };
      case 'done':
        return {
          type: 'done',
          title: typeof payload.title === 'string' ? payload.title : undefined,
        };
      case 'debug':
        return {
          type: 'debug',
          trace: payload.trace as ChatTurnDebugTrace,
        };
      default:
        return null;
    }
  } catch {
    if (eventName === 'done') {
      return { type: 'done' };
    }
    return null;
  }
}

export async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<ChatSseEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const throwIfAborted = () => {
    if (signal?.aborted) {
      void reader.cancel();
      throw new DOMException('Aborted', 'AbortError');
    }
  };

  try {
    while (true) {
      throwIfAborted();
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary).trim();
        buffer = buffer.slice(boundary + 2);
        if (block) {
          const event = parseEventBlock(block);
          if (event) {
            yield event;
          }
        }
        boundary = buffer.indexOf('\n\n');
      }
    }

    const tail = buffer.trim();
    if (tail) {
      const event = parseEventBlock(tail);
      if (event) {
        yield event;
      }
    }
  } finally {
    reader.releaseLock();
  }
}
